import React, { useCallback, useState } from 'react';
import {
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';

import fetchApi from '../../../saga/helpers/fetchApi';
import { executeEditSplit } from './executeSplit';
import { assess } from './assess';
import {
  cleanerIdOf,
  fetchPreflightData,
  fetchSplitContext,
  shouldCheck,
} from './fetchSplitContext';
import { STATUS, planSplit } from './planSplit';
import { findOverlaps } from './preflight';
import SplitReplacementDialog from './SplitReplacementDialog';

/**
 * Contrôle d'un `Remplacement` à l'enregistrement (plan 097 §16) : avertit,
 * ne refuse jamais de lui-même. `guard(values, proceed)` appelle `proceed()`
 * tout de suite s'il n'y a rien à dire (ou si le contrôle ne peut pas se
 * faire) ; sinon il ouvre le dialogue, où la personne choisit « Découper »,
 * « Enregistrer sans découper » ou « Annuler ».
 *
 * Exception : si R tel que saisi chevauche une autre affectation du
 * remplaçant, care refusera l'enregistrement (verificationChevauchement, à
 * chaque création ou modification). Le dialogue s'ouvre alors même sans
 * congé à découper, et « Enregistrer sans découper » est grisé.
 *
 * @param {Object} options
 * @param {boolean} options.create formulaire de création
 * @param {Object} options.record enregistrement chargé (édition)
 * @param {Function} options.onSplitCreate (values, pieces) → création des
 *   morceaux en un seul createBundle (formulaire de création)
 */
const useReplacementSplitGuard = ({ create, record, onSplitCreate }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirectTo = useRedirect();
  const refresh = useRefresh();
  const [state, setState] = useState(null);

  const guard = useCallback(
    async (values, proceed) => {
      if (!shouldCheck({ create, values, record })) {
        proceed();
        return;
      }
      setState({ phase: 'loading' });
      let context = null;
      try {
        context = await fetchSplitContext({ create, values, record });
      } catch (error) {
        setState(null);
        notify(
          `Contrôle du congé lié impossible (${error.message}) : enregistrement sans contrôle.`,
          'warning',
        );
        proceed();
        return;
      }
      const plan = context ? planSplit(context.input) : null;
      if (!plan) {
        setState(null);
        proceed();
        return;
      }
      // Données du pré-contrôle, lues une fois pour toute la période de R.
      let preflightData = null;
      let preflightError = null;
      try {
        preflightData = await fetchPreflightData({
          cleanerId: cleanerIdOf(values),
          replacementId: create ? null : record && record.id,
          start: context.replacement.start_date,
          end: context.replacement.end_date,
        });
      } catch (error) {
        preflightError = error.message;
      }
      const overlaps = preflightData
        ? findOverlaps(context.replacement, context.slots, preflightData.others)
        : [];
      if (plan.status === STATUS.COVERED && !overlaps.length) {
        setState(null);
        proceed();
        return;
      }
      setState({
        phase: 'warn',
        plan,
        context,
        values,
        proceed,
        choices: {},
        preflightData,
        preflightError,
      });
    },
    [create, record, notify],
  );

  const onCancel = useCallback(() => {
    setState(null);
  }, []);

  // Après un échec du createBundle, R a pu changer : on recharge l'écran.
  const onClose = useCallback(() => {
    const changed = state && state.result && state.result.stage === 'create';
    setState(null);
    if (changed) {
      refresh();
    }
  }, [state, refresh]);

  const onChoice = useCallback((key, value) => {
    setState((current) => {
      return {
        ...current,
        choices: { ...current.choices, [key]: value },
      };
    });
  }, []);

  const onSaveAnyway = useCallback(() => {
    const { proceed } = state;
    setState(null);
    proceed();
  }, [state]);

  const onSplit = useCallback(async () => {
    const { plan, context, values, choices, preflightData } = state;
    const view = assess({ plan, context, choices, preflightData, create });
    if (!view.canSplit) {
      return;
    }
    const pieces = view.resolved;
    if (create) {
      setState(null);
      onSplitCreate(values, pieces);
      return;
    }

    setState({ ...state, phase: 'working' });
    const result = await executeEditSplit({
      dataProvider,
      postJson: fetchApi,
      record,
      values,
      pieces,
    });
    if (result.ok) {
      setState(null);
      notify(
        pieces.length > 1
          ? `Remplacement découpé : ${pieces.length} remplacements, un par congé.`
          : 'Remplacement rattaché à son congé.',
        'info',
      );
      redirectTo('show', '/affectation', record.id);
      refresh();
      return;
    }
    setState({ ...state, phase: 'failed', result });
  }, [
    state,
    create,
    onSplitCreate,
    record,
    dataProvider,
    notify,
    redirectTo,
    refresh,
  ]);

  const dialog = (
    <SplitReplacementDialog
      state={state}
      create={create}
      onCancel={onCancel}
      onChoice={onChoice}
      onClose={onClose}
      onSaveAnyway={onSaveAnyway}
      onSplit={onSplit}
    />
  );

  return { guard, dialog };
};

export default useReplacementSplitGuard;
